import { useEffect, useMemo, useState } from 'react'
import { useLocation, useParams, useNavigate } from 'react-router-dom'
import { useSteelPlatesStore, INITIAL_STATE } from 'Store/RequestSteelPlates'
import { useSaleOrderStore } from 'Store/SaleOrder'
import { useFormik } from 'formik'
import { shallow } from 'zustand/shallow'
import { toast } from 'react-toastify'
import Swal from 'sweetalert2'
import moment from 'moment'
import * as Yup from 'yup'
import { REQUEST_STATUS_OPTIONS } from 'Configuration/Paco'

const VALIDATION_SCHEMA = Yup.object().shape({
  saleOrderId: Yup.number()
    .test('empty-check', 'กรุณาเลือก Sale Order', saleOrderId => !saleOrderId || saleOrderId !== 0)
    .required('กรุณาเลือก Sale Order'),
  quantity: Yup.number()
    .typeError('โปรดระบุเป็นตัวเลข')
    .min(1, 'ระบุจำนวนแผ่นเหล็ก')
    .required('ระบุจำนวนแผ่นเหล็ก'),
  width: Yup.number()
    .typeError('โปรดระบุเป็นตัวเลข')
    .min(1, 'ระบุความกว้าง')
    .required('ระบุความกว้าง'),
  length: Yup.number()
    .typeError('โปรดระบุเป็นตัวเลข')
    .min(1, 'ระบุความยาว')
    .required('ระบุความยาว'),
  thickness: Yup.number()
    .typeError('โปรดระบุเป็นตัวเลข')
    .min(1, 'ระบุความหนา')
    .required('ระบุความหนา'),
  requester: Yup.string().required('ระบุผู้ขอ'),
  requestedAt: Yup.date().required('ระบุวันที่ขอ'),
  requireDate: Yup.date().required('ระบุวันที่ต้องการ'),
})

const ViewModel = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { selected, setSelected, getOne, clearState } = useSteelPlatesStore(
    state => ({
      selected: state.selected,
      setSelected: state.setSelected,
      getOne: state.getOne,
      clearState: state.clearState,
    }),
    shallow
  )
  const { create, update } = useSteelPlatesStore(
    state => ({
      create: state.create,
      update: state.edit,
    }),
    shallow
  )
  const { saleOrder, getAllSaleOrder } = useSaleOrderStore(
    state => ({
      saleOrder: state.data,
      getAllSaleOrder: state.getAll,
    }),
    shallow
  )
  const isCreate = location.pathname.includes('create')
  const isView = location.search.includes('view')
  const [disabledSubmitButton, setDisabledSubmitButton] = useState(false)
  const [openJobStatusChanger, setOpenJobStatusChanger] = useState(false)

  const handleOpenJobStatusChanger = () => {
    setOpenJobStatusChanger(true)
  }

  const handleCloseJobStatusChanger = () => {
    setOpenJobStatusChanger(false)
  }

  const onChangeJobStatus = (status: string) => {
    if (status) {
      formik.setFieldValue('jobStatus', status)
    }
  }

  const onSubmitStatus = () => {
    if (formik.values.jobStatus) {
      update(parseInt(id ?? ''), { jobStatus: formik.values.jobStatus }).then(
        ({ success, data }) => {
          if (!success) {
            toast.error(data)
            return
          } else {
            Swal.fire({
              title: 'สำเร็จ',
              text: 'แก้ไขสถานะงานสำเร็จ',
              icon: 'success',
              confirmButtonText: 'ตกลง',
            })
            fetchData()
            handleCloseJobStatusChanger()
          }
        }
      )
    }
  }

  const fetchData = () => {
    getAllSaleOrder()

    if (!isCreate && id) {
      getOne(parseInt(id)).then(({ success, data }) => {
        if (!success) {
          Swal.fire({
            title: 'ดึงข้อมูลไม่สำเร็จ',
            text: 'โปรดลองใหม่อีกครั้ง',
            icon: 'error',
            confirmButtonText: 'ตกลง',
          }).then(() => {
            navigate('/request')
          })
        } else {
          formik.setValues({
            id: data.id,
            requestedAt: moment(data.requestedAt).toDate(),
            requireDate: moment(data.requireDate).toDate(),
            requester: data.requester,
            remark: data.remark,
            saleOrderId: data.project.saleOrderId,
            quantity: data.issueRequestSteelPlate.quantity,
            length: data.issueRequestSteelPlate.length,
            width: data.issueRequestSteelPlate.width,
            thickness: data.issueRequestSteelPlate.thickness,
            jobStatus: REQUEST_STATUS_OPTIONS[0].key,
          })
        }
      })
    }
  }

  const cancel = () => {
    navigate('/request')
    clearState()
  }

  const submit = async (values: any) => {
    setDisabledSubmitButton(true)

    if (isCreate) {
      const body: { [key: string]: any } = {
        saleOrderId: values.saleOrderId,
        quantity: values.quantity,
        width: values.width,
        length: values.length,
        thickness: values.thickness,
        requester: values.requester,
        requestedAt: moment(values.requestedAt).toISOString(),
        requireDate: moment(values.requireDate).toISOString(),
        remark: values.remark,
      }

      create(body).then(({ success, data }) => {
        if (!success) {
          toast.error(data)
          setDisabledSubmitButton(false)
          return
        } else {
          Swal.fire({
            title: 'สำเร็จ',
            text: 'สร้างรายการขอแผ่นเหล็กสำเร็จ',
            icon: 'success',
            confirmButtonText: 'ตกลง',
          }).then(() => {
            navigate('/request')
          })
        }
      })
    } else if (!isCreate && id) {
      const allFields = [
        'saleOrderId',
        'quantity',
        'width',
        'length',
        'thickness',
        'requester',
        'requestedAt',
        'requireDate',
        'remark',
      ]
      const body: { [key: string]: any } = {}

      allFields.forEach(field => {
        if (field === 'requestedAt' || field === 'requireDate') {
          if (!moment(selected[field]).isSame(values[field])) {
            body[field] = moment(values[field]).toISOString()
          }
        } else if (selected[field] !== values[field]) {
          body[field] = values[field]
        }
      })

      if (Object.keys(body).length === 0) {
        toast.error('ไม่พบการเปลี่ยนแปลง')
        setDisabledSubmitButton(false)
        return
      }

      update(parseInt(id), body).then(({ success, data }) => {
        if (!success) {
          toast.error(data)
          setDisabledSubmitButton(false)
          return
        } else {
          Swal.fire({
            title: 'สำเร็จ',
            text: 'แก้ไขรายการขอแผ่นเหล็กสำเร็จ',
            icon: 'success',
            confirmButtonText: 'ตกลง',
          })
          fetchData()
        }
      })
    }
  }

  const formik = useFormik({
    initialValues: INITIAL_STATE,
    validationSchema: VALIDATION_SCHEMA,
    onSubmit: submit,
  })

  const findedSaleOrder = useMemo(() => {
    return saleOrder.find(s => s.id === formik.values.saleOrderId)
  }, [formik.values.saleOrderId, saleOrder])

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    return () => {
      clearState()
      setSelected(INITIAL_STATE)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    saleOrder: saleOrder.map(s => ({
      label: `${s.code} - ${s.customerTitle}`,
      value: s.id,
    })),
    disabledSubmitButton,
    cancel,
    originalJobStatus: selected.jobStatus,
    formik,
    findedSaleOrder,
    isCreate,
    isView,
    openJobStatusChanger,
    handleOpenJobStatusChanger,
    handleCloseJobStatusChanger,
    onChangeJobStatus,
    onSubmitStatus,
  }
}

export default ViewModel
